<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ payroll.employee ? payroll.employee.name : $t('payroll_details') }}</h3>
          <div class="detail-page-actions">
            <button @click="printPayslip" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
              <AppIcon name="bi-printer" class="bi bi-printer" style="font-size:12px" />
              {{$t('print')}}
            </button>
            <button @click="downloadPayslip" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
              <AppIcon name="bi-download" class="bi bi-download" style="font-size:12px" />
              {{$t('download')}}
            </button>
            <button v-if="payroll.status !== 'paid' && helper.checkAccess('edit_payrolls') != -1" @click="editPayroll" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Payroll Details -->
      <div v-else-if="payroll && payroll.id">
        <div v-if="complianceWarnings.length" class="alert alert-warning">
          <strong>Bulletin incomplet :</strong>
          <ul class="mb-0 mt-1">
            <li v-for="warning in complianceWarnings" :key="warning">{{ warning }}</li>
          </ul>
        </div>
        <div class="row">
          <!-- Left Column -->
          <div class="col-md-8">
            <!-- Employee Information Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-person-badge" class="bi bi-person-badge me-2" />{{$t('employee_information')}}</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('reference')}}</small>
                    <strong>{{payroll.employee ? payroll.employee.reference : '-'}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('name')}}</small>
                    <strong>{{payroll.employee ? payroll.employee.name : '-'}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('position')}}</small>
                    <strong>{{payroll.employee ? payroll.employee.position : '-'}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('department')}}</small>
                    <strong>{{payroll.employee ? payroll.employee.department : '-'}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('period')}}</small>
                    <strong>{{formatPeriod(payroll.month)}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('status')}}</small>
                    <span class="badge rounded-pill" :class="{
                      'bg-success': payroll.status === 'paid',
                      'bg-secondary': payroll.status === 'draft'
                    }">
                      {{ $t(payroll.status) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Salary Breakdown Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-calculator" class="bi bi-calculator me-2" />{{$t('salary_breakdown')}}</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('basic_salary')}}</small>
                    <strong>{{formatMoney(payroll.basic_salary)}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('gross_salary')}}</small>
                    <strong>{{formatMoney(payroll.gross_salary)}}</strong>
                  </div>

                  <!-- Allowances -->
                  <div v-if="parsedAllowances.length > 0" class="col-md-12 mb-3">
                    <small class="text-success d-block mb-2">
                      <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{$t('allowances')}}
                    </small>
                    <div v-for="(allowance, index) in parsedAllowances" :key="'allow-'+index" 
                         class="d-flex justify-content-between align-items-center py-1 ps-3">
                      <small class="text-muted">{{ allowance.description }}</small>
                      <small class="text-success">+ {{ formatMoney(allowance.amount) }}</small>
                    </div>
                  </div>

                  <!-- Statutory Deductions -->
                  <div v-if="breakdown.statutory_deductions && breakdown.statutory_deductions.length > 0" class="col-md-12 mb-3">
                    <small class="text-danger d-block mb-2">
                      <AppIcon name="bi-dash-circle" class="bi bi-dash-circle me-1" />{{$t('statutory_deductions')}}
                    </small>
                    <div v-for="(deduction, index) in breakdown.statutory_deductions" :key="'stat-'+index" 
                         class="d-flex justify-content-between align-items-center py-1 ps-3">
                      <small class="text-muted">{{ deduction.name }} ({{ deduction.type }})</small>
                      <small class="text-danger">- {{ formatMoney(deduction.amount) }}</small>
                    </div>
                  </div>

                  <!-- Custom Deductions -->
                  <div v-if="parsedDeductions.length > 0" class="col-md-12 mb-3">
                    <small class="text-warning d-block mb-2">
                      <AppIcon name="bi-dash-circle" class="bi bi-dash-circle me-1" />{{$t('custom_deductions')}}
                    </small>
                    <div v-for="(deduction, index) in parsedDeductions" :key="'deduct-'+index" 
                         class="d-flex justify-content-between align-items-center py-1 ps-3">
                      <small class="text-muted">{{ deduction.description }}</small>
                      <small class="text-warning">- {{ formatMoney(deduction.amount) }}</small>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('total_tax')}}</small>
                    <strong class="text-danger">{{formatMoney(payroll.tax_amount)}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('total_social_security')}}</small>
                    <strong class="text-danger">{{formatMoney(payroll.social_security_amount)}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('total_deductions')}}</small>
                    <strong class="text-danger">{{formatMoney(totalDeductions)}}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{$t('net_salary')}}</small>
                    <strong class="text-success">{{formatMoney(payroll.net_salary)}}</strong>
                  </div>
                </div>
              </div>
            </div>

            <!-- Employer Costs Card -->
            <div v-if="breakdown.employer_costs && breakdown.employer_costs.length > 0" class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-building" class="bi bi-building me-2" />{{$t('employer_costs')}}</h6>
              </div>
              <div class="card-body">
                <p class="text-muted small mb-3">{{$t('employer_costs_description')}}</p>
                <div class="row">
                  <div v-for="(cost, index) in breakdown.employer_costs" :key="'emp-'+index" class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ cost.name }}</small>
                    <strong>{{ formatMoney(cost.amount) }}</strong>
                  </div>
                  <div class="col-md-12 mb-3">
                    <small class="text-muted d-block">{{$t('total_employer_cost')}}</small>
                    <strong class="text-primary">{{ formatMoney(totalEmployerCost) }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes Card -->
            <div v-if="payroll.notes" class="card">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
              </div>
              <div class="card-body">
                <p class="mb-0">{{ payroll.notes }}</p>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="col-md-4">
            <!-- Summary Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-wallet2" class="bi bi-wallet2 me-2" />{{$t('summary')}}</h6>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <small class="text-muted d-block">{{$t('gross_salary')}}</small>
                  <strong>{{formatMoney(payroll.gross_salary)}}</strong>
                </div>
                <div class="mb-3">
                  <small class="text-muted d-block">{{$t('total_deductions')}}</small>
                  <strong class="text-danger">- {{formatMoney(totalDeductions)}}</strong>
                </div>
                <div class="mt-3 pt-3 border-top text-center">
                  <small class="text-muted d-block">{{$t('net_salary')}}</small>
                  <h5 class="mb-0 text-success">
                    {{formatMoney(payroll.net_salary)}}
                  </h5>
                </div>
              </div>
            </div>

            <!-- Payment Info Card -->
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-credit-card" class="bi bi-credit-card me-2" />{{$t('payment')}}</h6>
              </div>
              <div class="card-body">
                <div class="mb-3" v-if="payroll.payment_date">
                  <small class="text-muted d-block">{{$t('payment_date')}}</small>
                  <strong>{{formatDate(payroll.payment_date)}}</strong>
                </div>
                <div v-if="payroll.payment_method">
                  <small class="text-muted d-block">{{$t('payment_method')}}</small>
                  <strong>{{payroll.payment_method}}</strong>
                </div>
                <div v-if="!payroll.payment_date && !payroll.payment_method">
                  <small class="text-muted">{{$t('no_payment_info')}}</small>
                </div>
              </div>
            </div>

            <!-- Metadata Card -->
            <div class="card">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-clock-history" class="bi bi-clock-history me-2" />{{$t('information')}}</h6>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <small class="text-muted d-block">{{$t('created_at')}}</small>
                  <strong>{{formatDate(payroll.created_at)}}</strong>
                </div>
                <div>
                  <small class="text-muted d-block">{{$t('updated_at')}}</small>
                  <strong>{{formatDate(payroll.updated_at)}}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper';
import PayslipPDFService from '@/services/PayslipPDFService';

export default {
  name: 'PayrollDetails',
  data() {
    return {
      helper,
      payroll: {},
      loading: true,
    };
  },
  computed: {
    parsedAllowances() {
      try {
        return JSON.parse(this.payroll.allowances || '[]');
      } catch (e) {
        return [];
      }
    },
    parsedDeductions() {
      try {
        return JSON.parse(this.payroll.deductions || '[]');
      } catch (e) {
        return [];
      }
    },
    breakdown() {
      try {
        const metadata = JSON.parse(this.payroll.metadata || '{}');
        return metadata.breakdown || {};
      } catch (e) {
        return {};
      }
    },
    payrollMetadata() {
      try {
        return JSON.parse(this.payroll.metadata || '{}');
      } catch (e) {
        return {};
      }
    },
    complianceWarnings() {
      const warnings = [...(this.payrollMetadata.compliance_warnings || [])];
      const context = this.payrollMetadata.payroll_context || {};
      if (!this.breakdown.rules_version) warnings.push('Version des règles légales absente');
      if (!context.employer_cnss_number || !context.employee_cnss_number) {
        warnings.push('Numéros CNSS manquants');
      }
      if (
        context.paid_days === undefined ||
        (context.sector !== 'agricultural' && context.regular_hours === undefined)
      ) {
        warnings.push('Jours ou heures payés manquants');
      }
      return [...new Set(warnings)];
    },
    payslipEmployee() {
      return this.payrollMetadata.employee_snapshot || this.payroll.employee || null;
    },
    totalEmployerCost() {
      if (!this.breakdown.employer_costs) return 0;
      return this.breakdown.employer_costs.reduce((sum, cost) => sum + parseFloat(cost.amount || 0), 0);
    },
    totalDeductions() {
      const customDeductions = this.parsedDeductions.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
      return customDeductions + parseFloat(this.payroll.tax_amount || 0) + parseFloat(this.payroll.social_security_amount || 0);
    },
    business() {
      return this.$store.getters.BUSINESS_DETAILS || { name: localStorage.getItem('businessName') || 'Company' };
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_payrolls') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_payrolls') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchPayroll();
  },
  methods: {
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },
    formatMoney(amount) {
      return helper.formatMoney(amount);
    },
    formatDate(date) {
      return helper.formatDate(date);
    },
    formatDateTime(datetime) {
      if (!datetime) return '-';
      const date = new Date(datetime);
      return date.toLocaleString('default', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatPeriod(monthStr) {
      if (!monthStr) return '-';
      const [year, month] = monthStr.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    },
    async fetchPayroll() {
      try {
        this.loading = true;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/payrolls/${this.$route.params.id}`);
        this.payroll = response.data.payroll;
        this.loading = false;
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: this.$t('failed_to_load_payroll')
        });
        this.loading = false;
        this.$router.push('/hr/payroll');
      }
    },
    editPayroll() {
      this.$router.push(`/hr/payroll?edit=${this.payroll.id}`);
    },
    printPayslip() {
      // Generate PDF and view it (opens in new tab for printing)
      if (!this.payslipEmployee) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'Employee information not loaded'
        });
        return;
      }
      
      try {
        PayslipPDFService.generatePayslip(
          this.payroll,
          this.payslipEmployee,
          this.business,
          'view'
        );
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'Failed to generate payslip PDF'
        });
      }
    },
    async downloadPayslip() {
      if (!this.payslipEmployee) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'Employee information not loaded'
        });
        return;
      }

      try {
        PayslipPDFService.generatePayslip(
          this.payroll,
          this.payslipEmployee,
          this.business,
          'download'
        );
        
        this.$swal({
          icon: 'success',
          title: this.$t('success'),
          text: this.$t('payslip_downloaded'),
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'Failed to download payslip PDF'
        });
      }
    }
  }
};
</script>

<style scoped>
.avatar-circle {
  transition: all 0.3s ease;
}

@media print {
  .btn, button {
    display: none !important;
  }
  .card {
    break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #dee2e6 !important;
  }
}
</style>
